import { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

// Utils
import { combineClasses, formatDayString, formatTimeString } from '@shared/utils';

// Constants
import { LABEL_LAST_LOGIN } from '@/constants';
import { EMPTY_FIELD_VALUE } from '@shared/constants';
const totalInfoVariants = cva('inline-flex items-center rounded-md p-4 text-4xs font-semibold transition-colors', {
  variants: {
    variant: {
      default: 'bg-white text-slate-700 hover:bg-slate-200 hover:text-slate-900',
      shadow: 'bg-white text-slate-700 shadow-lg shadow-primary-700/10'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
interface TotalInfoProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof totalInfoVariants> {
  label: string;
  icon: ReactNode;
  isUser: boolean;
  total?: number | string;
  date?: string;
}
const renderDateInfo = (date: string, isUser: boolean, isMobile = false) => {
  if (date === EMPTY_FIELD_VALUE) {
    return <span className={combineClasses('max-w-48 font-semibold', isUser ? 'text-md xs:text-lg md:text-3xs' : isMobile ? 'text-md xs:text-lg' : 'text-md')}>
        {EMPTY_FIELD_VALUE}
      </span>;
  }
  if (isUser || isMobile) {
    return <div className="flex gap-2">
        <span className={combineClasses('max-w-48 font-semibold', isUser ? 'text-md xs:text-lg md:text-3xs' : 'text-md xs:text-lg')}>
          {formatTimeString(date)}
        </span>
        <span className="text-primary-700 text-lg leading-4xs xs:leading-2xs md:leading-5xs font-semibold">
          .
        </span>
        <span className={combineClasses('max-w-48 font-semibold', isUser ? 'text-md xs:text-lg md:text-3xs' : 'text-md xs:text-lg')}>
          {formatDayString(date)}
        </span>
      </div>;
  }
  return <div className="flex-col hidden md:flex" data-sentry-component="renderDateInfo" data-sentry-source-file="index.tsx">
      <span className="text-md leading-4xs max-w-48 font-semibold">
        {formatTimeString(date)}
      </span>
      <span className="text-5xs leading-4xs max-w-48 font-normal">
        {formatDayString(date)}
      </span>
    </div>;
};
const TotalInfo = ({
  className,
  variant,
  label,
  total,
  icon,
  date,
  isUser,
  ...props
}: TotalInfoProps) => <div data-testid="total-info" className={combineClasses(totalInfoVariants({
  variant
}), 'flex items-center gap-1 rounded-xl w-full pointer-events-none', 'px-3 py-2 md:px-5 md:py-4', isUser && 'px-5 py-4', className)} {...props} data-sentry-component="TotalInfo" data-sentry-source-file="index.tsx">
    <div className={combineClasses('flex-center bg-primary-700 rounded-md', '[&_svg]:fill-white [&_svg]:w-[25px] [&_svg]:h-[25px]', 'size-10 md:size-12', isUser && 'size-12')}>
      {icon}
    </div>
    <div className="flex flex-col ml-2">
      {/* Label rendering */}
      {(isUser || !isUser && total && !date) && <span className="text-5xs max-w-48 font-normal whitespace-pre-line">
          {label}
        </span>}

      {/* Mobile last login label */}
      {!isUser && date && !total && <span className="text-5xs max-w-48 font-normal md:hidden">
          {LABEL_LAST_LOGIN}
        </span>}

      {/* Desktop label for date-only display */}
      {!isUser && date && !total && <span className={combineClasses('hidden md:flex', 'text-5xs max-w-48 font-normal')}>
          {label}
        </span>}

      {/* Total value display */}
      {!isUser && total && !date && <span className="text-xl leading-sm lg:text-3xl max-w-48 font-semibold">
          {total}
        </span>}

      {/* Date information rendering */}
      {isUser && date && !total && renderDateInfo(date, true)}

      {!isUser && date && !total && <>
          {/* Desktop date display */}
          <div className="hidden md:block">{renderDateInfo(date, false)}</div>

          {/* Mobile date display */}
          <div className="md:hidden">{renderDateInfo(date, false, true)}</div>
        </>}
    </div>
  </div>;
export { TotalInfo, totalInfoVariants };